import { useEffect, useMemo, useRef, type CSSProperties, type FocusEvent, type KeyboardEvent, type PointerEvent, useState } from 'react'
import { Compass, Inbox, MessageCircle, Phone, Plus, Video } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { isDesktop } from '@/lib/desktop'
import { useIsMobile } from '@/lib/mobile'
import { useAuthStore } from '@/store/auth'
import { authApi } from '@/lib/auth'
import { channelName, useSocialStore } from '@/store/social'
import { useVoiceStore } from '@/store/voice'
import { channelMuted, guildMuted, useGuildStore } from '@/store/guilds'
import { DM_SETTINGS, dmMentions } from '@/lib/muting'
import { avatarUrl, guildIconUrl } from '@/lib/avatar'
import { guildColor, guildInitials, isUnread, moveInOrder, plural } from '@/lib/guildUi'
import { forumActivity } from '@/lib/forums'
import { dmRailEntries } from '@/lib/dmRail'
import { ChannelType } from '@/types/api'
import { cx } from '@/lib/cx'
import { AddGuildModal } from './guild/AddGuildModal'
import { GuildContextMenu } from './guild/GuildContextMenu'
import { GuildActivity } from './GuildActivity'
import { Avatar } from './Avatar'
import { GroupAvatar } from './GroupAvatar'
import { GuildHoverContent, RAIL_TOOLTIP_ID, RailTooltip, type HoverAnchor } from './GuildHoverCard'
import styles from './GuildRail.module.css'

const EMPTY: string[] = []

/** Над чем карточка: сервер (название и кто в голосе) или простая кнопка с подписью. */
type HoverTarget = { kind: 'guild'; id: string } | { kind: 'label'; label: string }

export function GuildRail() {
  const mobile = useIsMobile()
  const hasTitlebar = isDesktop && !mobile
  const view = useAppStore((s) => s.view)
  const setView = useAppStore((s) => s.setView)
  const showContent = useAppStore((s) => s.showContent)
  const openGuild = useAppStore((s) => s.openGuild)
  // заглушённые беседы значок не зажигают — ради этого их и глушат
  const dmSettings = useGuildStore((s) => s.settings[DM_SETTINGS])
  const mentions = useSocialStore((s) => dmMentions(s.readStates, (id) => Boolean(s.channels[id]), dmSettings))
  const readStates = useSocialStore((s) => s.readStates)
  const privateChannels = useSocialStore((s) => s.channels)
  const calls = useSocialStore((s) => s.calls)
  const relationships = useSocialStore((s) => s.relationships)
  const selfId = useSocialStore((s) => s.currentUserId)
  const activeCallId = useVoiceStore((s) => s.connection?.guildId === null ? s.connection.channelId : null)
  const callVideo = useVoiceStore((s) => s.camera || Object.keys(s.cameras).length > 0)
  const dmEntries = useMemo(() => dmRailEntries({ channels: privateChannels, readStates, calls, relationships, settings: dmSettings, selfId, activeCallId }), [privateChannels, readStates, calls, relationships, dmSettings, selfId, activeCallId])
  const pendingIn = useSocialStore((s) => Object.values(s.relationships).filter((r) => r.type === 3).length)
  const guilds = useGuildStore((s) => s.guilds)
  const channels = useGuildStore((s) => s.channels)
  const settings = useGuildStore((s) => s.settings)
  const adding = useAppStore((s) => s.addGuildOpen)
  const setAdding = useAppStore((s) => s.setAddGuildOpen)
  const [menu, setMenu] = useState<{ guildId: string; x: number; y: number; unread: boolean } | null>(null)
  const order = useAuthStore((s) => s.user?.guild_order ?? EMPTY)
  const setOrder = useAuthStore((s) => s.setGuildOrder)
  // id тащимого держим и в ref: события dragover/drop прилетают раньше, чем React перерисует
  const dragRef = useRef<string | null>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)
  const [hover, setHover] = useState<{ target: HoverTarget; anchor: HoverAnchor } | null>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const homeBadge = mentions + pendingIn
  const homeActive = view.kind === 'home'

  const cancelHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current)
    hideTimer.current = null
  }
  useEffect(() => () => {
    if (hideTimer.current) clearTimeout(hideTimer.current)
  }, [])
  const hideNow = () => {
    cancelHide()
    setHover(null)
  }
  // Между значками зазор в 8px: карточка ждёт чуть-чуть и переезжает к следующему значку,
  // а не пропадает и не вылезает заново с анимацией на каждом.
  const hideSoon = () => {
    cancelHide()
    hideTimer.current = setTimeout(() => {
      hideTimer.current = null
      setHover(null)
    }, 80)
  }
  const showFor = (target: HoverTarget, el: HTMLElement) => {
    cancelHide()
    // пока тащат сервер или открыто меню, карточка только мешала бы
    if (dragRef.current || menu) return
    const r = el.getBoundingClientRect()
    setHover({ target, anchor: { top: r.top, bottom: r.bottom, right: r.right } })
  }
  const tip = (target: HoverTarget) => ({
    // касание — не наведение: на телефоне карточка всплывала бы от тапа и оставалась висеть
    onPointerEnter: (e: PointerEvent<HTMLElement>) => {
      if (e.pointerType !== 'touch') showFor(target, e.currentTarget)
    },
    onPointerLeave: hideSoon,
    // с клавиатуры карточка та же; фокус от клика мышью её не открывает — там уже было наведение
    onFocus: (e: FocusEvent<HTMLElement>) => {
      if (e.currentTarget.matches(':focus-visible')) showFor(target, e.currentTarget)
    },
    onBlur: hideSoon,
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Escape') hideNow()
    },
  })

  // порядок: сначала расставленный вручную, за ним новые серверы по времени вступления
  const list = useMemo(() => {
    const byJoin = Object.values(guilds).sort((a, b) => (a.joined_at ?? '').localeCompare(b.joined_at ?? '') || (a.id < b.id ? -1 : 1))
    if (!order.length) return byJoin
    const rank = new Map(order.map((id, i) => [id, i]))
    return byJoin.sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity))
  }, [guilds, order])

  // с какой стороны от цели встанет сервер: тащим сверху вниз — ниже цели, снизу вверх — выше
  const dropSide = (items: typeof list, dragging: string | null, target: string): 'up' | 'down' | null => {
    if (!dragging || dragging === target) return null
    const ids = items.map((g) => g.id)
    return ids.indexOf(dragging) < ids.indexOf(target) ? 'down' : 'up'
  }

  const dropAt = (target: string) => {
    const dragging = dragRef.current
    if (!dragging || dragging === target) return
    const ids = moveInOrder(list.map((g) => g.id), dragging, target)
    setOrder(ids)
    void authApi.setGuildOrder(ids).catch(() => undefined)
  }

  return (
    // колонку прокрутили — значок из-под карточки уехал
    <nav className={styles.rail} aria-label="Серверы" onScroll={hover ? hideNow : undefined}>
      <button
        type="button"
        className={cx(styles.item, styles.home)}
        {...tip({ kind: 'label', label: 'Личные сообщения' })}
        aria-label="Личные сообщения"
        aria-current={homeActive ? 'page' : undefined}
        data-selected={homeActive ? '' : undefined}
        onClick={() => setView(view.kind === 'home' ? view : { kind: 'home', page: 'friends' })}
      >
        <span className={styles.pill} data-unread={homeBadge ? '' : undefined} />
        <span className={styles.icon}>
          <MessageCircle size={26} strokeWidth={2} />
        </span>
        {homeBadge ? <span className={styles.badge}>{homeBadge}</span> : null}
      </button>
      {dmEntries.map(({ channel, unread, inCall, ringing }) => {
        const name = channelName(channel)
        const group = channel.type === ChannelType.GroupDM
        const recipient = group ? undefined : channel.recipients?.[0]
        const selected = view.kind === 'home' && 'channelId' in view && view.channelId === channel.id
        const callLabel = inCall ? 'Идёт звонок' : ringing ? 'Входящий звонок' : ''
        const label = [name, callLabel, unread ? `${unread} ${plural(unread, 'непрочитанное сообщение', 'непрочитанных сообщения', 'непрочитанных сообщений')}` : ''].filter(Boolean).join(', ')
        return (
          <button
            key={channel.id}
            type="button"
            className={styles.item}
            {...tip({ kind: 'label', label })}
            aria-label={label}
            aria-describedby={hover?.target.kind === 'label' && hover.target.label === label ? RAIL_TOOLTIP_ID : undefined}
            aria-current={selected ? 'page' : undefined}
            data-selected={selected ? '' : undefined}
            data-unread={unread ? '' : undefined}
            onClick={() => { hideNow(); setView({ kind: 'home', channelId: channel.id }) }}
          >
            <span className={styles.pill} />
            <span className={cx(styles.icon, styles.dmIcon)}>
              {group ? (
                <GroupAvatar recipients={channel.recipients} size={40} />
              ) : (
                <Avatar initial={name.slice(0, 1).toUpperCase()} color={recipient ? guildColor(recipient.id) : '#5865F2'} size={recipient?.avatar_decoration_data ? 32 : 40} src={recipient ? avatarUrl(recipient.id, recipient.avatar) : undefined} decoration={recipient?.avatar_decoration_data} />
              )}
            </span>
            {inCall || ringing ? (
              <span className={cx(styles.activity, inCall && styles.activityMine)} aria-hidden="true">
                {inCall && callVideo ? <Video size={12} /> : <Phone size={12} />}
              </span>
            ) : null}
            {unread ? <span className={styles.badge}>{unread > 99 ? '99+' : unread}</span> : null}
          </button>
        )
      })}
      <div className={styles.separator} role="separator" />
      {list.map((g) => {
        const isSelected = view.kind === 'guild' && view.guildId === g.id
        let unread = false
        let badge = 0
        const s = settings[g.id]
        for (const ch of Object.values(channels[g.id] ?? {})) {
          if (ch.type === ChannelType.GuildForum) {
            const activity = forumActivity(ch, channels[g.id] ?? {}, readStates, s)
            unread ||= activity.unread
            badge += activity.mentions
            continue
          }
          if (ch.type !== ChannelType.GuildText && ch.type !== ChannelType.GuildVoice) continue
          const rs = readStates[ch.id]
          if (isUnread(ch, rs) && !channelMuted(s, ch.id, channels[g.id])) unread = true
          badge += rs?.mention_count ?? 0
        }
        const mutedGuild = guildMuted(s)
        return (
          <button
            key={g.id}
            type="button"
            className={styles.item}
            {...tip({ kind: 'guild', id: g.id })}
            aria-label={g.name}
            aria-describedby={hover?.target.kind === 'guild' && hover.target.id === g.id ? RAIL_TOOLTIP_ID : undefined}
            aria-current={isSelected ? 'page' : undefined}
            data-selected={isSelected ? '' : undefined}
            data-unread={unread ? '' : undefined}
            data-muted={mutedGuild ? '' : undefined}
            data-dragging={dragId === g.id ? '' : undefined}
            data-over={overId === g.id && dragId !== g.id ? (dropSide(list, dragId, g.id) ?? undefined) : undefined}
            draggable={!mobile}
            onDragStart={(e) => {
              hideNow()
              dragRef.current = g.id
              setDragId(g.id)
              e.dataTransfer.effectAllowed = 'move'
              // Safari не начинает перетаскивание без данных
              e.dataTransfer.setData('text/plain', g.id)
              // браузер по умолчанию тащит снимок всей кнопки — получается квадрат;
              // берём саму иконку, она круглая и обрезает картинку по кругу
              const icon = e.currentTarget.querySelector<HTMLElement>('[data-icon]')
              if (icon) e.dataTransfer.setDragImage(icon, icon.offsetWidth / 2, icon.offsetHeight / 2)
            }}
            onDragOver={(e) => {
              if (!dragRef.current) return
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
              setOverId(g.id)
            }}
            onDragLeave={() => setOverId((id) => (id === g.id ? null : id))}
            onDrop={(e) => {
              e.preventDefault()
              dropAt(g.id)
              dragRef.current = null
              setDragId(null)
              setOverId(null)
            }}
            onDragEnd={() => {
              dragRef.current = null
              setDragId(null)
              setOverId(null)
            }}
            onClick={() => openGuild(g.id)}
            onContextMenu={(e) => {
              e.preventDefault()
              hideNow()
              setMenu({ guildId: g.id, x: e.clientX, y: e.clientY, unread: unread || badge > 0 })
            }}
            // цвет сервера — только под инициалами: у картинки с прозрачным краем он просвечивал бы рамкой
            style={guildIconUrl(g.id, g.icon) ? undefined : ({ '--icon-bg': guildColor(g.id) } as CSSProperties)}
          >
            <span className={styles.pill} />
            <span className={styles.icon} data-icon="">
              {guildIconUrl(g.id, g.icon) ? <img draggable={false} className={styles.iconImage} src={guildIconUrl(g.id, g.icon)} alt="" /> : guildInitials(g.name)}
            </span>
            <GuildActivity guildId={g.id} />
            {badge ? <span className={styles.badge}>{badge}</span> : null}
          </button>
        )
      })}
      {!hasTitlebar ? <button type="button" className={cx(styles.item, styles.action)} data-selected={view.kind === 'home' && 'page' in view && view.page === 'inbox' ? '' : undefined} {...tip({ kind: 'label', label: 'Входящие' })} aria-label="Входящие" onClick={() => { hideNow(); setView({ kind: 'home', page: 'inbox' }); showContent() }}>
        <span className={styles.icon}><Inbox size={24} /></span>
      </button> : null}
      <button type="button" className={cx(styles.item, styles.action)} {...tip({ kind: 'label', label: 'Добавить сервер' })} aria-label="Добавить сервер" onClick={() => { hideNow(); setAdding(true) }}>
        <span className={styles.icon}>
          <Plus size={24} />
        </span>
      </button>
      <button
        type="button"
        className={cx(styles.item, styles.action)}
        data-selected={view.kind === 'discovery' ? '' : undefined}
        {...tip({ kind: 'label', label: 'Каталог серверов' })}
        aria-label="Каталог серверов"
        onClick={() => { setView({ kind: 'discovery' }); showContent() }}
      >
        <span className={styles.icon}>
          <Compass size={24} />
        </span>
      </button>
      {hover && (hover.target.kind === 'label' || guilds[hover.target.id]) ? (
        <RailTooltip anchor={hover.anchor} label={hover.target.kind === 'label'}>
          {hover.target.kind === 'guild' ? <GuildHoverContent guildId={hover.target.id} /> : hover.target.label}
        </RailTooltip>
      ) : null}
      {adding ? <AddGuildModal onClose={() => setAdding(false)} /> : null}
      {menu && guilds[menu.guildId] ? (
        <GuildContextMenu guild={guilds[menu.guildId]!} x={menu.x} y={menu.y} unread={menu.unread} onClose={() => setMenu(null)} />
      ) : null}
    </nav>
  )
}
